/** Admin (Super Admin) API calls. All go through the same `/api` base. */

const API_BASE: string = import.meta.env.VITE_API_BASE_URL ?? "/api";

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { accept: "application/json", ...(init?.headers ?? {}) },
  });
  if (!res.ok) {
    let detail = "";
    try {
      const body = (await res.json()) as { message?: string };
      detail = body.message ?? "";
    } catch {
      /* ignore */
    }
    throw new Error(detail || `HTTP ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

const jsonInit = (method: string, body: unknown): RequestInit => ({
  method,
  headers: { "content-type": "application/json" },
  body: JSON.stringify(body),
});

// --- types (loose — shaped by the API) ---------------------------------

export interface ProgrammeListRow {
  id: string;
  title: string;
  status: string;
  _count: { tracks: number };
}
export interface Programme {
  id: string;
  title: string;
  description: string | null;
  status: string;
  coverImageBlobRef: string | null;
  coverImageUrl: string | null;
  tracks: Array<{ id: string; title: string; order: number; status: string }>;
}
export interface Track {
  id: string;
  title: string;
  order: number;
  status: string;
  flow: string;
  passThresholdPercent: number;
  programme: { id: string; title: string; status: string };
  modules: Array<{ id: string; title: string; order: number; required: boolean; status: string }>;
  prerequisites: Array<{ requiredTrackId: string }>;
}
export interface TrackListRow {
  id: string;
  programmeId: string;
  title: string;
  status: string;
  programme: { title: string };
}
export interface Module {
  id: string;
  title: string;
  order: number;
  required: boolean;
  status: string;
  track: { id: string; title: string; status: string; programmeId: string };
  learningItems: Array<{
    id: string;
    title: string;
    type: string;
    order: number;
    status: string;
    downloadAllowed: boolean;
    htmlPackage: { entryFile: string } | null;
  }>;
}
export interface LearningItem {
  id: string;
  title: string;
  type: string;
  order: number;
  status: string;
  downloadAllowed: boolean;
  externalUrl: string | null;
  htmlPackage: { entryFile: string; uploadedAt: string } | null;
  resources: Array<{ filename: string; createdAt: string }>;
  module: { id: string; title: string; status: string; trackId: string };
}
export interface LearnerRow {
  id: string;
  email: string;
  displayName: string;
  status: string;
  organisation: { id: string; name: string };
  _count: { enrollments: number };
}

// --- programmes -------------------------------------------------------

export const listProgrammes = () => req<ProgrammeListRow[]>("/mgmt/programmes");
export const getProgramme = (id: string) => req<Programme>(`/mgmt/programmes/${id}`);
export const createProgramme = (b: { title: string; description?: string }) =>
  req<{ id: string }>("/mgmt/programmes", jsonInit("POST", b));
export const updateProgramme = (id: string, b: Partial<{ title: string; description: string }>) =>
  req(`/mgmt/programmes/${id}`, jsonInit("PATCH", b));
export async function uploadProgrammeCoverImage(id: string, file: File): Promise<Programme> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`${API_BASE}/mgmt/programmes/${id}/cover-image`, {
    method: "POST",
    body: form,
  });
  if (!res.ok) {
    let detail = "";
    try {
      detail = ((await res.json()) as { message?: string }).message ?? "";
    } catch {
      /* ignore */
    }
    throw new Error(detail || `HTTP ${res.status}`);
  }
  return (await res.json()) as Programme;
}

// --- tracks ----------------------------------------------------------

export const getTrack = (id: string) => req<Track>(`/mgmt/tracks/${id}`);
export const listTracks = (programmeId?: string) =>
  req<TrackListRow[]>(`/mgmt/tracks${programmeId ? `?programmeId=${programmeId}` : ""}`);
export const createTrack = (b: {
  programmeId: string;
  title: string;
  order: number;
  flow?: string;
  passThresholdPercent?: number;
}) => req<{ id: string }>("/mgmt/tracks", jsonInit("POST", b));
export const updateTrack = (
  id: string,
  b: Partial<{ title: string; order: number; flow: string; passThresholdPercent: number }>,
) => req(`/mgmt/tracks/${id}`, jsonInit("PATCH", b));
export const setPrerequisites = (id: string, requiredTrackIds: string[]) =>
  req(`/mgmt/tracks/${id}/prerequisites`, jsonInit("PUT", { requiredTrackIds }));
export const deleteTrack = (id: string) => req(`/mgmt/tracks/${id}`, { method: "DELETE" });

// --- modules -------------------------------------------------------

export const getModule = (id: string) => req<Module>(`/mgmt/modules/${id}`);
export const createModule = (b: {
  trackId: string;
  title: string;
  order: number;
  required?: boolean;
}) => req<{ id: string }>("/mgmt/modules", jsonInit("POST", b));
export const updateModule = (
  id: string,
  b: Partial<{ title: string; order: number; required: boolean }>,
) => req(`/mgmt/modules/${id}`, jsonInit("PATCH", b));
export const deleteModule = (id: string) => req(`/mgmt/modules/${id}`, { method: "DELETE" });

// --- learning items ----------------------------------------------

export const getLearningItem = (id: string) => req<LearningItem>(`/mgmt/learning-items/${id}`);
export const createLearningItem = (b: {
  moduleId: string;
  type: string;
  title: string;
  order: number;
  downloadAllowed?: boolean;
  externalUrl?: string;
}) => req<{ id: string }>("/mgmt/learning-items", jsonInit("POST", b));
export const updateLearningItem = (
  id: string,
  b: Partial<{ title: string; order: number; downloadAllowed: boolean; externalUrl: string }>,
) => req(`/mgmt/learning-items/${id}`, jsonInit("PATCH", b));

export async function uploadHtmlPackage(
  itemId: string,
  file: File,
): Promise<{ entryFile: string; fileCount: number }> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`${API_BASE}/mgmt/learning-items/${itemId}/html-package`, {
    method: "POST",
    body: form,
  });
  if (!res.ok) {
    let detail = "";
    try {
      detail = ((await res.json()) as { message?: string }).message ?? "";
    } catch {
      /* ignore */
    }
    throw new Error(detail || `HTTP ${res.status}`);
  }
  return (await res.json()) as { entryFile: string; fileCount: number };
}

export async function uploadResource(itemId: string, file: File): Promise<{ filename: string }> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`${API_BASE}/mgmt/learning-items/${itemId}/resource`, {
    method: "POST",
    body: form,
  });
  if (!res.ok) {
    let detail = "";
    try {
      detail = ((await res.json()) as { message?: string }).message ?? "";
    } catch {
      /* ignore */
    }
    throw new Error(detail || `HTTP ${res.status}`);
  }
  return (await res.json()) as { filename: string };
}

// --- publish -------------------------------------------------------

const RESOURCE: Record<string, string> = {
  programme: "programmes",
  track: "tracks",
  module: "modules",
  learningItem: "learning-items",
};
export const setPublished = (
  entity: "programme" | "track" | "module" | "learningItem",
  id: string,
  publish: boolean,
) =>
  req(`/mgmt/${RESOURCE[entity]}/${id}/${publish ? "publish" : "unpublish"}`, { method: "POST" });

// --- organisations ------------------------------------------------

export interface OrganisationRow {
  id: string;
  name: string;
  isWithAWill: boolean;
  status: string;
  logoBlobRef: string | null;
  _count: { users: number; enrollments: number };
}
export const listOrganisations = () => req<OrganisationRow[]>("/mgmt/organisations");
export const createOrganisation = (b: { name: string }) =>
  req<{ id: string; name: string }>("/mgmt/organisations", jsonInit("POST", b));

export async function uploadOrganisationLogo(
  orgId: string,
  file: File,
): Promise<{ logoUrl: string }> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`${API_BASE}/mgmt/organisations/${orgId}/logo`, {
    method: "POST",
    body: form,
  });
  if (!res.ok) {
    let detail = "";
    try {
      detail = ((await res.json()) as { message?: string }).message ?? "";
    } catch {
      /* ignore */
    }
    throw new Error(detail || `HTTP ${res.status}`);
  }
  return (await res.json()) as { logoUrl: string };
}

// --- learners & assignment --------------------------------------

export const listLearners = () => req<LearnerRow[]>("/mgmt/learners");
export const createLearner = (b: { email: string; displayName: string; organisationId: string }) =>
  req<{ userId: string; invitationToken: string; inviteUrl: string }>(
    "/mgmt/learners",
    jsonInit("POST", b),
  );
export const assignProgramme = (b: { learnerId: string; programmeId: string }) =>
  req<{ enrollmentsCreated: number; tracks: string[] }>("/mgmt/assignments", jsonInit("POST", b));

// --- assessment: question banks, questions, config ----------------

export interface QuestionOption {
  id: string;
  text: string;
}
export interface QuestionRow {
  id: string;
  bankId: string;
  type: string;
  stem: string;
  options: QuestionOption[];
  correctOptionIds: string[];
  explanation: string | null;
  category: string | null;
  difficulty: string | null;
  status: string;
}
export interface QuestionBankRow {
  id: string;
  title: string;
  trackId: string | null;
  _count?: { questions: number };
  track?: { title: string };
}
export interface QuestionBankDetail extends QuestionBankRow {
  questions: QuestionRow[];
}

export const listQuestionBanks = (trackId?: string) =>
  req<QuestionBankRow[]>(`/mgmt/question-banks${trackId ? `?trackId=${trackId}` : ""}`);
export const getQuestionBank = (id: string) =>
  req<QuestionBankDetail>(`/mgmt/question-banks/${id}`);
export const createQuestionBank = (b: { title: string; trackId: string; purpose?: string }) =>
  req<{ id: string }>("/mgmt/question-banks", jsonInit("POST", b));

export const createQuestion = (b: {
  bankId: string;
  type: string;
  stem: string;
  options: QuestionOption[];
  correctOptionIds: string[];
  explanation?: string;
  category?: string;
  difficulty?: string;
}) => req<{ id: string }>("/mgmt/questions", jsonInit("POST", b));
export const updateQuestion = (
  id: string,
  b: Partial<{
    type: string;
    stem: string;
    options: QuestionOption[];
    correctOptionIds: string[];
    explanation: string;
    category: string;
    difficulty: string;
  }>,
) => req(`/mgmt/questions/${id}`, jsonInit("PATCH", b));
export const setQuestionPublished = (id: string, publish: boolean) =>
  req(`/mgmt/questions/${id}/${publish ? "publish" : "unpublish"}`, { method: "POST" });
export const setQuestionsPublished = (ids: string[], publish: boolean) =>
  req<{ updated: number }>("/mgmt/questions/bulk-publish", jsonInit("POST", { ids, publish }));

export interface ImportQuestionsResult {
  imported: number;
  skipped: Array<{ row: number; reason: string }>;
}
export async function importQuestions(bankId: string, file: File): Promise<ImportQuestionsResult> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`${API_BASE}/mgmt/question-banks/${bankId}/import`, {
    method: "POST",
    body: form,
  });
  if (!res.ok) {
    let detail = "";
    try {
      detail = ((await res.json()) as { message?: string }).message ?? "";
    } catch {
      /* ignore */
    }
    throw new Error(detail || `HTTP ${res.status}`);
  }
  return (await res.json()) as ImportQuestionsResult;
}

export interface AssessmentConfigRow {
  id: string;
  trackId: string;
  bankId: string;
  passPercent: number;
  maxAttempts: number;
  selectionMode: string;
  questionsPresented: number;
  randomiseOrder: boolean;
  onFinalFailure: string;
}
export const getAssessmentConfig = (trackId: string) =>
  req<AssessmentConfigRow | null>(`/mgmt/tracks/${trackId}/assessment-config`);
export const saveAssessmentConfig = (
  trackId: string,
  b: {
    bankId: string;
    passPercent: number;
    maxAttempts: number;
    selectionMode: string;
    questionsPresented: number;
    randomiseOrder: boolean;
    onFinalFailure: string;
  },
) => req<AssessmentConfigRow>(`/mgmt/tracks/${trackId}/assessment-config`, jsonInit("PUT", b));

export interface DiagnosticConfigRow {
  id: string;
  trackId: string;
  bankId: string;
  questionCount: number;
  requirePre: boolean;
  requirePost: boolean;
}
export const getDiagnosticConfig = (trackId: string) =>
  req<DiagnosticConfigRow | null>(`/mgmt/tracks/${trackId}/diagnostic-config`);
export const saveDiagnosticConfig = (
  trackId: string,
  b: { bankId: string; questionCount: number; requirePre: boolean; requirePost: boolean },
) => req<DiagnosticConfigRow>(`/mgmt/tracks/${trackId}/diagnostic-config`, jsonInit("PUT", b));

// --- certificates: templates + credential retry ----------------------

export interface CertificateSignatory {
  name: string;
  title: string;
  imageRef?: string | null;
}
export interface CertificateTemplateRow {
  id: string;
  title: string;
  programmeId: string | null;
  bodyHtml: string;
  signatories: CertificateSignatory[];
  showScore: boolean;
  active: boolean;
  programme?: { title: string } | null;
}

export const listCertificateTemplates = (programmeId?: string) =>
  req<CertificateTemplateRow[]>(
    `/mgmt/certificate-templates${programmeId ? `?programmeId=${programmeId}` : ""}`,
  );
export const createCertificateTemplate = (b: {
  title: string;
  programmeId: string | null;
  bodyHtml: string;
  signatories: CertificateSignatory[];
  showScore: boolean;
}) => req<CertificateTemplateRow>("/mgmt/certificate-templates", jsonInit("POST", b));
export const updateCertificateTemplate = (
  id: string,
  b: Partial<{
    title: string;
    bodyHtml: string;
    signatories: CertificateSignatory[];
    showScore: boolean;
    active: boolean;
  }>,
) => req<CertificateTemplateRow>(`/mgmt/certificate-templates/${id}`, jsonInit("PATCH", b));
export async function uploadSignatoryImage(
  templateId: string,
  index: number,
  file: File,
): Promise<CertificateTemplateRow> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(
    `${API_BASE}/mgmt/certificate-templates/${templateId}/signatories/${index}/signature`,
    { method: "POST", body: form },
  );
  if (!res.ok) {
    let detail = "";
    try {
      detail = ((await res.json()) as { message?: string }).message ?? "";
    } catch {
      /* ignore */
    }
    throw new Error(detail || `HTTP ${res.status}`);
  }
  return (await res.json()) as CertificateTemplateRow;
}

// --- feedback templates -------------------------------------------------

export interface FeedbackQuestion {
  id: string;
  text: string;
  type: "RATING_5" | "NPS_0_10" | "TEXT" | "SINGLE_CHOICE";
  options?: string[];
}
export interface FeedbackTemplateRow {
  id: string;
  title: string;
  programmeId: string | null;
  questions: FeedbackQuestion[];
  active: boolean;
  programme?: { title: string } | null;
}

export const listFeedbackTemplates = (programmeId?: string) =>
  req<FeedbackTemplateRow[]>(
    `/mgmt/feedback-templates${programmeId ? `?programmeId=${programmeId}` : ""}`,
  );
export const createFeedbackTemplate = (b: {
  title: string;
  programmeId: string | null;
  questions: FeedbackQuestion[];
}) => req<FeedbackTemplateRow>("/mgmt/feedback-templates", jsonInit("POST", b));
export const updateFeedbackTemplate = (
  id: string,
  b: Partial<{ title: string; questions: FeedbackQuestion[]; active: boolean }>,
) => req<FeedbackTemplateRow>(`/mgmt/feedback-templates/${id}`, jsonInit("PATCH", b));

export interface FailedCredentialRow {
  id: string;
  state: string;
  credentialId: string | null;
  lastError: string | null;
  attemptCount: number;
  enrollment: {
    id: string;
    learner: { displayName: string; email: string };
    track: { title: string; programme: { title: string } };
  };
}
export const listFailedCredentials = () => req<FailedCredentialRow[]>("/mgmt/credentials/failed");
export const retryCredential = (enrollmentId: string) =>
  req<{ state: string; credentialId: string | null; lastError: string | null }>(
    `/mgmt/credentials/${enrollmentId}/retry`,
    { method: "POST" },
  );

export interface MissingCredentialRow {
  enrollmentId: string;
  learnerName: string;
  email: string;
  trackTitle: string;
  programmeTitle: string;
  enrollmentState: string;
}
export const listMissingCredentials = () =>
  req<MissingCredentialRow[]>("/mgmt/credentials/missing");
export const reconcileCredential = (enrollmentId: string) =>
  req<{ state: string; credentialId: string | null; lastError: string | null }>(
    `/mgmt/credentials/${enrollmentId}/reconcile`,
    { method: "POST" },
  );

// --- reporting ---------------------------------------------------------

export interface LearnerProgressRow {
  enrollmentId: string;
  learnerName: string;
  email: string;
  organisation: string;
  programmeTitle: string;
  trackTitle: string;
  progressPercent: number;
  currentModule: string | null;
  assessmentScore: number | null;
  attempts: number;
  passFail: "PASS" | "FAIL" | null;
  completionDate: string | null;
  certificateId: string | null;
}

export const getLearnerProgressReport = () =>
  req<LearnerProgressRow[]>("/mgmt/reports/learner-progress");

// --- notifications -----------------------------------------------------

export interface FailedNotificationRow {
  id: string;
  kind: string;
  to: string;
  error: string | null;
  createdAt: string;
}

export const listFailedNotifications = () =>
  req<FailedNotificationRow[]>("/mgmt/notifications/failed");
export const retryNotification = (id: string) =>
  req<{ status: string; error: string | null }>(`/mgmt/notifications/${id}/retry`, {
    method: "POST",
  });
